/**
 * Boundary between the UI's `QueryField` display rows and the backend's
 * `QueryFieldType` / `QueryFieldInput` shapes.
 *
 * Read: `toQueryFieldRows` pairs a display row with a retained copy of the
 * raw `QueryFieldType` so the write path can round-trip un-edited fields
 * verbatim — today's only mutation is delete (drop the row).
 *
 */

import plur from "plur";
import { deriveDateRangeDisplay } from "../../../shared/DatePeriodSection/datePeriodDisplay";
import { FALLBACK_QUERY_ROLLUP_LEVEL_OPTIONS } from "../../../shared/levels/fallbackCatalogue";
import {
  deriveLevelColumnDisplay,
  deriveLevelFullDisplay,
  normalizeLevelStack,
} from "../../../shared/levels/levelDisplay";
import { normalizeAttributePath } from "../../../shared/RulesSection/attributeCatalogue";
import { toSentenceCaseLabel } from "../../../shared/sentenceCaseLabel";
import type {
  QueryField,
  QueryFieldDataFormat,
  QueryFieldLevel,
  QueryFieldType as QueryFieldUiType,
} from "./types";
import type { QueryRollupLevelOption } from "../../../shared/levels/types";
import type { RuleConditionSource, RuleSetSource } from "../../../shared/RulesSection/mappers";
import type {
  DatePeriodInput,
  FieldSettingsInput,
  QueryFieldComponentFieldsFragment,
  QueryFieldComponentInput,
  QueryFieldFieldsFragment,
  QueryFieldInput,
  QueryFieldKind,
  RuleConditionInput,
  RuleGroupInput,
  RuleSetInput,
} from "~/graphql/supergraphTypes";

// -- Row type ----------------------------------------------------------------

/** Display row + raw companion. Grid reads `display`; save reads `raw`. */
export type QueryFieldRow = {
  display: QueryField;
  raw: QueryFieldFieldsFragment;
};

// -- Component tree (FORMULA fields) ------------------------------------------

/** Read-shape node of a FORMULA field's per-instance component tree. The
 * generated fragment type is unrolled to the service's max component-tree
 * depth (10, see `queries.federation.gql`); this recursive alias is what the
 * mappers traverse — the unrolled shape is assignable to it. Anchors
 * (sub-formula usages) carry `children`; leaves carry the config. */
export type QueryFieldComponentTreeNode = QueryFieldComponentFieldsFragment & {
  children?: ReadonlyArray<QueryFieldComponentTreeNode>;
};

/** Write-shape node: `applyFieldSettingsToRaw` synthesizes these (`id: 0` —
 * the server reassigns row ids on save and the input mapper drops them).
 * `children` is required/mutable so a built tree stays assignable to the
 * fragment's unrolled `components` field on `QueryFieldFieldsFragment`. */
export type QueryFieldComponentTreeNodeWrite = QueryFieldComponentFieldsFragment & {
  children: QueryFieldComponentTreeNodeWrite[];
};

// -- Enum display maps -------------------------------------------------------

const REFERENCE_KIND_TO_FIELD_TYPE: Record<QueryFieldKind, QueryFieldUiType> = {
  NODE: "Node",
  FORMULA: "Formula",
  FORMULA_COMPONENT: "Formula",
  PRIMARY_FIELD: "Primary field",
  DYNAMIC_DATE: "Dynamic date",
};

const deriveDataType = (
  settings: QueryFieldFieldsFragment["settings"],
  referenceKind: QueryFieldKind,
): QueryField["dataType"] => {
  if (settings?.fieldType) return settings.fieldType;
  if (referenceKind === "DYNAMIC_DATE") return "DATE";
  if (referenceKind === "NODE") return "VALUE";
  if (referenceKind !== "PRIMARY_FIELD") return "QUANTITY";
  return null;
};

// -- Slug helper --------------------------------------------------------------

const NON_SLUG_CHARS_RE = /[^a-z0-9]+/g;
// Drop non ascii characters rather than replacing with a separator
const NON_ASCII_RE = /[\u0080-\uFFFF]/g;
const FALLBACK_SLUG = "field";

/** Snake-case a name for use in a merge token.
 *
 * Query-field tags are allocated by the backend, so this is only used by
 * `footnotesMapper` for its interim client-side footnote tokens — see the
 * RPRT-4032 note there. */
export const aliasToSnake = (alias: string): string => {
  let slug = alias
    .normalize("NFKD")
    .replace(NON_ASCII_RE, "")
    .toLowerCase()
    .replace(NON_SLUG_CHARS_RE, "_")
    .replace(/^_+|_+$/g, "");
  if (!slug) return FALLBACK_SLUG;
  if (!/^[a-z]/.test(slug)) {
    slug = `f_${slug}`;
  }
  return slug;
};

/**
 * Generic Carbone repetition array. The report worker nests
 * every result row under this key; templates loop a whole page region over it
 * with `{d.records[i]...}` / `{d.records[i+1]...}` to render one page per row
 * (e.g. one page per position in a consolidated account statement). The name is
 * deliberately type-agnostic so future per-account/per-contact pages need no
 * change here or in the worker/template.
 */
export const TEMPLATE_RECORDS_ARRAY = "records";

/**
 * Container key holding the nested per-transaction objects on a page record.
 * Transaction-level field values live under `record.transactions[*]` rather
 * than inline on the record, so their template tags carry an extra loop
 * segment.
 */
export const TEMPLATE_TRANSACTIONS_ARRAY = "transactions";

/** Level identifiers that place a field's values under the nested
 * `transactions` array (canonical identifier + its API alias). */
const TRANSACTION_LEVEL_IDENTIFIERS: ReadonlySet<string> = new Set([
  "transaction",
  "transaction_id",
]);

export const deriveIsTransactionLevel = (
  levels: QueryFieldFieldsFragment["levels"] | null,
): boolean => (levels?.levels ?? []).some((level) => TRANSACTION_LEVEL_IDENTIFIERS.has(level));

/**
 * Wrap a bare merge field `{d.<inner>}` into the loop-qualified, template-ready
 * tag `{d.records[i].<inner>}` — or `{d.records[i].transactions[i].<inner>}`
 * for transaction-level fields — for display and copy. This is a **pure
 * presentation** decoration: it must never be written back into
 * `raw.mergeField` / `display.mergeField`, which have to stay byte-identical
 * to the tag the backend stored so the write path can echo it back. A value
 * that doesn't begin with `{d.` — including the empty tag a not-yet-saved
 * field carries — is returned unchanged.
 */
export const toTemplateTag = (mergeField: string, isTransactionLevel: boolean): string =>
  mergeField.startsWith("{d.")
    ? `{d.${TEMPLATE_RECORDS_ARRAY}[i].${
        isTransactionLevel ? `${TEMPLATE_TRANSACTIONS_ARRAY}[i].` : ""
      }${mergeField.slice("{d.".length)}`
    : mergeField;

// -- Display summaries -------------------------------------------------------

const deriveDataFormat = (
  settings: QueryFieldFieldsFragment["settings"],
): QueryFieldDataFormat => {
  if (!settings) return "Default";
  return settings.overrideDisplayFormat ? "Custom" : "Default";
};

const deriveLevel = (
  levels: QueryFieldFieldsFragment["levels"] | null,
  rollupLevelOptions: ReadonlyArray<QueryRollupLevelOption>,
): QueryFieldLevel => deriveLevelColumnDisplay(levels?.levels ?? [], rollupLevelOptions);

const deriveLevelFull = (
  levels: QueryFieldFieldsFragment["levels"] | null,
  rollupLevelOptions: ReadonlyArray<QueryRollupLevelOption>,
): string => deriveLevelFullDisplay(levels?.levels ?? [], rollupLevelOptions);

const deriveDateRange = (
  datePeriod: QueryFieldFieldsFragment["datePeriod"],
): Pick<QueryField, "dateRange" | "hasDateOffset" | "offsetMonths"> => {
  const display = deriveDateRangeDisplay(datePeriod);
  return {
    dateRange: display.label,
    hasDateOffset: display.hasOffset,
    offsetMonths: display.offsetMonths,
  };
};

/** Serialize the identity-bearing date-period columns for structural
 * comparison. Labels are NOT compared — two different custom ranges both
 * display "Custom date range" but are distinct configs. `null` (leaf never
 * configured) is its own distinct value. */
const datePeriodIdentity = (datePeriod: QueryFieldFieldsFragment["datePeriod"]): string =>
  datePeriod
    ? JSON.stringify([
        datePeriod.periodKind,
        datePeriod.dateType,
        datePeriod.offsetMonths ?? 0,
        datePeriod.trailingMonths ?? null,
        datePeriod.customRangeKind ?? null,
        datePeriod.customStartParameter ?? null,
        datePeriod.customStartDate ?? null,
        datePeriod.customEndParameter ?? null,
        datePeriod.customEndDate ?? null,
      ])
    : "unconfigured";

/** Serialize the identity-bearing level stack for structural comparison.
 * Stacks are normalized to canonical values first so a label-form and a
 * value-form of the same level compare equal. `null` (leaf never configured)
 * is its own distinct value. */
const levelStackIdentity = (
  levels: QueryFieldComponentTreeNode["levels"],
  options: ReadonlyArray<QueryRollupLevelOption>,
): string =>
  levels ? JSON.stringify(normalizeLevelStack(levels.levels ?? [], options)) : "unconfigured";

const collectLeaves = (
  nodes: ReadonlyArray<QueryFieldComponentTreeNode>,
  out: Array<QueryFieldComponentTreeNode>,
): void => {
  for (const node of nodes) {
    const children = node.children ?? [];
    if (children.length > 0) {
      collectLeaves(children, out);
    } else {
      out.push(node);
    }
  }
};

/** FORMULA fields carry no field-level date period; the column summarizes
 * the per-leaf component config instead: a config shared by every leaf
 * renders its label (same as a node row), divergent configs render
 * "Multiple", and an empty tree renders "--". Only persisted/buffered
 * leaves participate — a formula edit that adds a component isn't
 * reflected here until the settings panel next saves the field (the
 * panel's seed/prune pass rebuilds the full expansion). */
const deriveFormulaDateRange = (
  components: ReadonlyArray<QueryFieldComponentTreeNode> | null | undefined,
): Pick<QueryField, "dateRange" | "hasDateOffset" | "offsetMonths"> => {
  const leaves: Array<QueryFieldComponentTreeNode> = [];
  collectLeaves(components ?? [], leaves);
  if (leaves.length === 0) return deriveDateRange(null);

  const [first, ...rest] = leaves;
  const firstIdentity = datePeriodIdentity(first.datePeriod ?? null);
  const allSame = rest.every(
    (leaf) => datePeriodIdentity(leaf.datePeriod ?? null) === firstIdentity,
  );
  if (allSame) return deriveDateRange(first.datePeriod ?? null);

  return { dateRange: "Multiple", hasDateOffset: false, offsetMonths: 0 };
};

/** Level analogue of `deriveFormulaDateRange`: shared leaf stack renders like
 * a node row, divergent stacks render "Multiple", empty tree renders "--".
 * Same persisted/buffered-leaves limitation applies. */
const deriveFormulaLevel = (
  components: ReadonlyArray<QueryFieldComponentTreeNode> | null | undefined,
  rollupLevelOptions: ReadonlyArray<QueryRollupLevelOption>,
): Pick<QueryField, "level" | "levelFull" | "isTransactionLevel"> => {
  const leaves: Array<QueryFieldComponentTreeNode> = [];
  collectLeaves(components ?? [], leaves);
  if (leaves.length === 0) return { level: "--", levelFull: "--", isTransactionLevel: false };

  const [first, ...rest] = leaves;
  const firstIdentity = levelStackIdentity(first.levels, rollupLevelOptions);
  const allSame = rest.every(
    (leaf) => levelStackIdentity(leaf.levels, rollupLevelOptions) === firstIdentity,
  );
  if (allSame) {
    return {
      level: deriveLevel(first.levels, rollupLevelOptions),
      levelFull: deriveLevelFull(first.levels, rollupLevelOptions),
      isTransactionLevel: deriveIsTransactionLevel(first.levels),
    };
  }

  return { level: "Multiple", levelFull: "Multiple", isTransactionLevel: false };
};

const countRuleConditions = (ruleSet: RuleSetSource | null | undefined): number =>
  (ruleSet?.groups ?? []).reduce((sum, group) => sum + group.conditions.length, 0);

const formatRuleCount = (count: number): string =>
  count === 0 ? "--" : `${count} ${plur("rule", count)}`;

const pickRuleSet = (
  settings: QueryFieldFieldsFragment["settings"],
  kind: "filter" | "suppression",
): RuleSetSource | null =>
  settings ? (kind === "filter" ? settings.filterRules : settings.suppressionRules) : null;

const deriveRulesSummary = (
  settings: QueryFieldFieldsFragment["settings"],
  kind: "filter" | "suppression",
): string => formatRuleCount(countRuleConditions(pickRuleSet(settings, kind)));

/** Rules analogue of the formula column summaries: the count is the TOTAL
 * number of filter (resp. suppression) rules across every leaf component,
 * zero renders "--". Same persisted/buffered-leaves limitation applies. */
const deriveFormulaRulesSummary = (
  components: ReadonlyArray<QueryFieldComponentTreeNode> | null | undefined,
  kind: "filter" | "suppression",
): string => {
  const leaves: Array<QueryFieldComponentTreeNode> = [];
  collectLeaves(components ?? [], leaves);
  const total = leaves.reduce(
    (sum, leaf) => sum + countRuleConditions(pickRuleSet(leaf.settings ?? null, kind)),
    0,
  );
  return formatRuleCount(total);
};

// -- Read path ---------------------------------------------------------------

export const toDisplay = (
  raw: QueryFieldFieldsFragment,
  variant: number,
  rollupLevelOptions: ReadonlyArray<QueryRollupLevelOption> = FALLBACK_QUERY_ROLLUP_LEVEL_OPTIONS,
): QueryField => ({
  id: String(raw.id),
  name: raw.alias,
  variant,
  // Verbatim: the tag the backend stored is the tag the user sees, so an
  // alias rename never appears to move a live template's binding. Empty
  // until a newly added field is saved and the backend allocates one.
  mergeField: raw.mergeField,
  fieldType: REFERENCE_KIND_TO_FIELD_TYPE[raw.referenceKind],
  dataType: deriveDataType(raw.settings, raw.referenceKind),
  ...(raw.referenceKind === "FORMULA"
    ? deriveFormulaDateRange(raw.components)
    : deriveDateRange(raw.datePeriod)),
  ...(raw.referenceKind === "FORMULA"
    ? deriveFormulaLevel(raw.components, rollupLevelOptions)
    : {
        level: deriveLevel(raw.levels, rollupLevelOptions),
        levelFull: deriveLevelFull(raw.levels, rollupLevelOptions),
        isTransactionLevel: deriveIsTransactionLevel(raw.levels),
      }),
  dataFormat: deriveDataFormat(raw.settings),
  filterRules:
    raw.referenceKind === "FORMULA"
      ? deriveFormulaRulesSummary(raw.components, "filter")
      : deriveRulesSummary(raw.settings, "filter"),
  suppressionRules:
    raw.referenceKind === "FORMULA"
      ? deriveFormulaRulesSummary(raw.components, "suppression")
      : deriveRulesSummary(raw.settings, "suppression"),
});

/** The field's name as the UI shows it. A primary field's alias is the raw
 * column identifier (`legal_name`), which is sentence-cased for display; every
 * other kind carries an alias the user chose. Anywhere a field is named to the
 * user — grid, settings header, footnote merge tags — goes through this. */
export const getNameDisplayText = (field: QueryField): string =>
  field.fieldType === "Primary field" ? toSentenceCaseLabel(field.name) : field.name;

const bySortOrder = (a: QueryFieldFieldsFragment, b: QueryFieldFieldsFragment): number =>
  a.sortOrder - b.sortOrder;

/** Number the rows of each alias 1, 2, 3… in row order, for the grid's
 * "Variant" column.
 *
 * This is a display counter only — it is not the numeric suffix of the merge
 * tag. The backend allocates suffixes above every one it has ever issued, so a
 * field tagged `{d.commitment_7}` can sit at Variant 1. Recompute whenever the
 * row list changes, since deleting a row renumbers the ones after it. */
export const assignDisplayVariants = (rows: ReadonlyArray<QueryFieldRow>): QueryFieldRow[] => {
  const counts = new Map<string, number>();
  return rows.map((row) => {
    const variant = (counts.get(row.raw.alias) ?? 0) + 1;
    counts.set(row.raw.alias, variant);
    if (row.display.variant === variant) return row;
    return { ...row, display: { ...row.display, variant } };
  });
};

/** Convert the `finpubQuery.fields` array into display rows. */
export const toQueryFieldRows = (
  fields: ReadonlyArray<QueryFieldFieldsFragment> | null | undefined,
  rollupLevelOptions: ReadonlyArray<QueryRollupLevelOption> = FALLBACK_QUERY_ROLLUP_LEVEL_OPTIONS,
): QueryFieldRow[] => {
  if (!fields) return [];
  const sorted = [...fields].sort(bySortOrder);
  return assignDisplayVariants(
    sorted.map((raw) => ({ display: toDisplay(raw, 1, rollupLevelOptions), raw })),
  );
};

// -- Write path --------------------------------------------------------------
// Display rows are lossy (e.g. `dataFormat: "Custom"` has no concrete
// payload), so the write side serializes the raw companion. Add/edit UX
// (RPRT-3080/3081) will synthesize new `raw` entries.

const toDatePeriodInput = (
  datePeriod: QueryFieldFieldsFragment["datePeriod"],
): DatePeriodInput | null => {
  if (!datePeriod) return null;

  const { periodKind, dateType, offsetMonths, trailingMonths, customRangeKind } = datePeriod;

  // Mirror the BE `_per_kind_invariants`: fields that don't belong to the
  // active periodKind / customRangeKind must be null. Server-persisted data
  // may violate these invariants if it predates the reconcile logic, so we
  // start from an all-null base and re-add only the fields the active kind owns.
  const base: DatePeriodInput = {
    periodKind,
    dateType,
    offsetMonths,
    trailingMonths: null,
    customRangeKind: null,
    customStartParameter: null,
    customStartDate: null,
    customEndParameter: null,
    customEndDate: null,
  };

  if (periodKind !== "CUSTOM") {
    return periodKind === "TRAILING_MONTHS" ? { ...base, trailingMonths } : base;
  }

  const isRange = customRangeKind === "RANGE";
  return {
    ...base,
    customRangeKind: customRangeKind ?? "SINGLE",
    customStartParameter: datePeriod.customStartParameter,
    customStartDate: datePeriod.customStartDate,
    customEndParameter: isRange ? datePeriod.customEndParameter : null,
    customEndDate: isRange ? datePeriod.customEndDate : null,
  };
};

const toRuleConditionInput = (condition: RuleConditionSource): RuleConditionInput => {
  const attribute = typeof condition.attribute === "string" ? condition.attribute : "";
  return {
    attribute: normalizeAttributePath(attribute),
    operator: condition.operator,
    value: condition.value,
    // A `RUNTIME_INPUT` condition's value is a token name rather than a
    // filter value; dropping the kind here would make the backend validate
    // the token as a literal and reject it.
    valueKind: condition.valueKind ?? "LITERAL",
    sortOrder: condition.sortOrder,
  };
};

const toRuleGroupInput = (group: RuleSetSource["groups"][number]): RuleGroupInput => ({
  conditionOperator: group.conditionOperator,
  sortOrder: group.sortOrder,
  conditions: group.conditions.map(toRuleConditionInput),
});

const toRuleSetInput = (ruleSet: RuleSetSource | null | undefined): RuleSetInput | null => {
  if (!ruleSet) return null;
  return {
    groupsOperator: ruleSet.groupsOperator,
    groups: ruleSet.groups.map(toRuleGroupInput),
  };
};

const toFieldSettingsInput = (
  settings: QueryFieldFieldsFragment["settings"],
): FieldSettingsInput | null => {
  if (!settings) return null;
  return {
    fieldType: settings.fieldType,
    overrideDecimalPlaces: settings.overrideDecimalPlaces,
    decimalPlaces: settings.decimalPlaces,
    overrideDisplayFormat: settings.overrideDisplayFormat,
    negativeNumberStyle: settings.negativeNumberStyle,
    numberScale: settings.numberScale,
    thousandsSeparator: settings.thousandsSeparator,
    showCurrencySymbol: settings.showCurrencySymbol,
    currencySymbolPosition: settings.currencySymbolPosition,
    zeroDisplayStyle: settings.zeroDisplayStyle,
    nullDisplayStyle: settings.nullDisplayStyle,
    dateFormatPattern: settings.dateFormatPattern,
    filterRules: toRuleSetInput(settings.filterRules),
    suppressionRules: toRuleSetInput(settings.suppressionRules),
  };
};

const toLevelStackInput = (
  levels: QueryFieldFieldsFragment["levels"] | null,
): QueryFieldInput["levels"] => {
  if (!levels) return null;
  return { levels: levels.levels ? [...levels.levels] : [] };
};

const toQueryFieldComponentInput = (
  node: QueryFieldComponentTreeNode,
): QueryFieldComponentInput => ({
  formulaComponentId: node.formulaComponentId,
  settings: toFieldSettingsInput(node.settings),
  datePeriod: toDatePeriodInput(node.datePeriod),
  levels: toLevelStackInput(node.levels),
  children: (node.children ?? []).map(toQueryFieldComponentInput),
});

const toInput = (row: QueryFieldRow, sortOrder: number): QueryFieldInput => ({
  referenceKind: row.raw.referenceKind,
  alias: row.raw.alias,
  // Identity + retention signal. A non-empty tag means the field exists on
  // the current version, so echoing it tells the backend to keep that exact
  // tag through the copy-on-write update (an alias rename does not renumber
  // it). An empty tag means a newly synthesized row: omit the key entirely so
  // the backend allocates a fresh suffix above every one it has ever issued.
  // Echoing a tag the current version does not have is a 422.
  mergeField: row.raw.mergeField.trim() === "" ? undefined : row.raw.mergeField,
  typeHierarchyNodeId: row.raw.typeHierarchyNodeId,
  formulaId: row.raw.formulaId,
  // `primaryField` is the canonical "<table>.<column>" input — `primaryFieldId`
  // is deprecated on both read (always null) and write (ignored).
  primaryField: row.raw.primaryField,
  dynamicDate: row.raw.referenceKind === "DYNAMIC_DATE" ? row.raw.dynamicDate : null,
  sortOrder,
  settings: toFieldSettingsInput(row.raw.settings),
  // Dynamic dates are injected at execute time; they never carry a period.
  datePeriod:
    row.raw.referenceKind === "DYNAMIC_DATE" ? null : toDatePeriodInput(row.raw.datePeriod),
  levels: toLevelStackInput(row.raw.levels),
  components: row.raw.components.map(toQueryFieldComponentInput),
});

/** Re-assigns `sortOrder` from row order so drag-reorder (RPRT-3081)
 * persists without callers threading indices. */
export const toQueryFieldInputs = (rows: ReadonlyArray<QueryFieldRow>): QueryFieldInput[] =>
  rows.map((row, index) => toInput(row, index));
